import { Link } from "react-router";
import {
  ACCURACY,
  AGE_GATE,
  GAMBLING_HELP,
  LIFELINE,
  POSTURE,
  REQUIRED_FOOTER_LINE,
} from "../content/compliance";
import { LEGAL_ENTITY, SUPPORT } from "../content/legal";
import { Container } from "./Container";
import styles from "./Footer.module.css";

/**
 * Persistent footer. Renders on EVERY route, signed in or out.
 *
 * This is a compliance surface before it is a layout element:
 *   §06   the required line, verbatim
 *   §8C   persistent 18+ / gamble-responsibly messaging
 *   §3.1  Gambling Help Online and Lifeline, always reachable
 *
 * The accuracy line sits with them by design (Ned, 2026-07-31): betting warning,
 * wellbeing warning and accuracy warning answer the same question for a reader
 * about to spend money on a number.
 *
 * Don't make any of this conditional on route or auth state.
 */
export function Footer() {
  return (
    <footer className={styles.footer}>
      <Container>
        <div className={styles.inner}>
          <p className={styles.required}>{REQUIRED_FOOTER_LINE}</p>

          <div className={styles.row}>
            <span className={styles.age} aria-label="Eighteen plus">
              {AGE_GATE}
            </span>
            <p className={styles.support}>
              <strong>{GAMBLING_HELP.name}</strong>{" "}
              <a href={`tel:${GAMBLING_HELP.phone.replace(/\s/g, "")}`}>
                {GAMBLING_HELP.phone}
              </a>
              , free and confidential, 24/7. <strong>{LIFELINE.name}</strong>{" "}
              <a href={`tel:${LIFELINE.phone.replace(/\s/g, "")}`}>{LIFELINE.phone}</a>.
            </p>
          </div>

          <div className={styles.divider} />

          <p className={styles.posture}>{POSTURE}</p>
          <p className={styles.posture}>{ACCURACY}</p>

          {/*
            "Site", because the row is now three things: pages about the company,
            the legal documents, and the way to reach a person. Two branches
            renamed this label independently — one to "Legal and support" when
            the support link arrived, one to "Site" when About and Meet the team
            did — and both were reaching for the same point: the label is what a
            screen-reader user hears when deciding whether this is the nav they
            want, so it has to cover everything in it. "Site" does; the other two
            each describe a subset.

            About and Meet the team live here rather than in the main nav because
            that bar fits four items at 390px only because none of them wraps.
          */}
          <nav className={styles.links} aria-label="Site">
            <Link to="/about">About</Link>
            <Link to="/team">Meet the team</Link>
            <Link to="/legal/terms">Terms</Link>
            <Link to="/legal/privacy">Privacy</Link>
            <Link to="/legal/responsible-gambling">Responsible Gambling</Link>
            {/* The way to reach a person, on every page, signed in or out.
                NOT className={styles.support} — that class is the Gambling Help
                / Lifeline paragraph above, and this is customer support. Two
                different things that would otherwise share a name in a file
                where confusing them matters. */}
            <a href={`mailto:${LEGAL_ENTITY.contactGeneral}`} title={SUPPORT.linkTitle}>
              {SUPPORT.label}
            </a>

            {/*
              Back to top, sitting with the links because this is where a reader
              who has run out of page actually is.

              ScrollToTop already handles the case of FOLLOWING one of these
              links — the reason it exists is that clicking "Privacy" from down
              here used to open Privacy scrolled to its middle. What it cannot
              handle is clicking the link for the page you are already on:
              react-router sees no path change, so no navigation effect runs and
              the control appears dead. This covers that, and the plain "I've
              read to the bottom and want the nav back" case on Terms, which is
              several thousand words long.
            */}
            <button type="button" className={styles.toTop} onClick={scrollToTop}>
              Back to top
            </button>
          </nav>
        </div>
      </Container>
    </footer>
  );
}

/**
 * Smooth here, instant in ScrollToTop, and the difference is intentional.
 *
 * This is a scroll WITHIN the page the reader is already on, so the motion
 * tells them where they went — a jump-cut from the bottom of Terms to the top
 * of Terms is disorienting because the content is the same. A route change is
 * the opposite case: the content is entirely new, so animating past it is just
 * a delay. `prefers-reduced-motion` still wins over both.
 */
function scrollToTop() {
  const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  window.scrollTo({ top: 0, left: 0, behavior: reduced ? "instant" : "smooth" });
  /* Move keyboard focus with the viewport. Without this, scrolling to the top
     leaves the focus ring in the footer, so the next Tab carries the reader
     straight back down to where they just left. */
  document.getElementById("main")?.focus({ preventScroll: true });
}
